import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { format, isToday, isTomorrow, parseISO, differenceInDays } from "date-fns";
import { fi } from "date-fns/locale";
import { MapPin, Package, Clock, CheckCircle2, AlertCircle, Truck, ChevronRight, Calendar, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import { handleError, handleSuccess } from "@/lib/error-handler";
import { useNavigate } from "react-router-dom";
import { 
  INSTALLATION_STATUS_LABELS, 
  INSTALLATION_STATUS_COLORS, 
  TASK_TYPE_LABELS 
} from "@/config/constants";
import { getCustomerDisplayName, getDateLabel as getDateLabelFormatter } from "@/lib/formatters";

interface TodayTask {
  id: string;
  status: string;
  task_type: string;
  notes: string | null;
  load_position: number | null;
  orders: {
    id: string;
    order_number: string;
    customers: {
      first_name: string | null;
      last_name: string | null;
      company_name: string | null;
      phone: string | null;
    } | null;
  } | null;
  cemeteries: {
    name: string;
    city: string;
    address: string;
  } | null;
  installation_loads: {
    id: string;
    load_date: string;
    status: string;
  } | null;
}

interface LoadSummary {
  id: string;
  load_date: string;
  status: string;
  total_distance_km: number | null;
  estimated_duration_min: number | null;
  installation_count: number;
}

export function InstallerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch today's tasks
  const { data: todayTasks, isLoading: loadingTasks } = useQuery({
    queryKey: ["installer-today-tasks", user?.id],
    queryFn: async () => {
      const today = format(new Date(), "yyyy-MM-dd");
      
      const { data, error } = await supabase
        .from("installations")
        .select(`
          id,
          status,
          task_type,
          notes,
          load_position,
          orders (
            id,
            order_number,
            customers (
              first_name,
              last_name,
              company_name,
              phone
            )
          ),
          cemeteries (
            name,
            city,
            address
          ),
          installation_loads!inner (
            id,
            load_date,
            status
          )
        `)
        .eq("installer_id", user?.id ?? "")
        .eq("installation_loads.load_date", today)
        .is("deleted_at", null)
        .order("load_position", { ascending: true });

      if (error) throw error;
      return data as TodayTask[];
    },
    enabled: !!user?.id,
  });

  // Fetch upcoming loads
  const { data: upcomingLoads, isLoading: loadingLoads } = useQuery({
    queryKey: ["installer-upcoming-loads", user?.id],
    queryFn: async () => {
      const today = format(new Date(), "yyyy-MM-dd");
      
      const { data, error } = await supabase
        .from("installation_loads")
        .select(`
          id,
          load_date,
          status,
          total_distance_km,
          estimated_duration_min,
          installations (id)
        `)
        .eq("installer_id", user?.id ?? "")
        .gte("load_date", today)
        .is("deleted_at", null)
        .order("load_date", { ascending: true })
        .limit(5);

      if (error) throw error;
      
      return data.map((load) => ({
        ...load,
        installation_count: load.installations?.length || 0,
      })) as LoadSummary[];
    },
    enabled: !!user?.id,
  });

  // Quick status update mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "pending" | "in_progress" | "completed" | "cancelled" | "partial" | "skipped" }) => {
      const { error } = await supabase
        .from("installations")
        .update({ 
          status,
          completed_date: status === "completed" ? format(new Date(), "yyyy-MM-dd") : null
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["installer-today-tasks"] });
      handleSuccess("Tila päivitetty");
    },
    onError: (error) => {
      handleError(error, { title: "Virhe tilan päivityksessä" });
    },
  });

  // Using getCustomerDisplayName and getDateLabel from formatters

  const completedCount = todayTasks?.filter((t) => t.status === "completed").length || 0;
  const totalCount = todayTasks?.length || 0;

  return (
    <div className="space-y-6">
      {/* Today's Summary */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Päivän tehtävät</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCount}</div>
            <p className="text-xs text-muted-foreground">
              {completedCount} / {totalCount} valmis
            </p>
            <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: totalCount > 0 ? `${(completedCount / totalCount) * 100}%` : "0%" }}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Seuraava tehtävä</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {todayTasks && todayTasks.find((t) => t.status !== "completed") ? (
              (() => {
                const next = todayTasks.find((t) => t.status !== "completed");
                return (
                  <>
                    <div className="text-lg font-semibold truncate">
                      {next?.cemeteries?.name}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {next?.cemeteries?.city}
                    </p>
                  </>
                );
              })()
            ) : (
              <div className="text-muted-foreground">Ei tehtäviä</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Tulevat kuormat</CardTitle>
            <Truck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{upcomingLoads?.length || 0}</div>
            <p className="text-xs text-muted-foreground">
              tällä viikolla
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Today's Tasks List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Päivän tehtävät</CardTitle>
              <CardDescription>
                {format(new Date(), "EEEE d. MMMM yyyy", { locale: fi })}
              </CardDescription>
            </div>
            <Button variant="outline" onClick={() => navigate("/loads/mine")}>
              Kaikki kuormat
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingTasks ? (
            <div className="text-center py-8 text-muted-foreground">Ladataan...</div>
          ) : !todayTasks || todayTasks.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-2" />
              <p className="text-muted-foreground">Ei tänään suunniteltuja tehtäviä</p>
            </div>
          ) : (
            <div className="space-y-3">
              {todayTasks.map((task, index) => (
                <div
                  key={task.id}
                  className={cn(
                    "p-4 rounded-lg border transition-colors",
                    task.status === "completed" && "bg-muted/50 opacity-75",
                    task.status === "in_progress" && "border-primary bg-primary/5"
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                          {task.load_position || index + 1}
                        </span>
                        <span className="font-medium">{task.orders?.order_number}</span>
                        <Badge variant="secondary" className="text-xs">
                          {TASK_TYPE_LABELS[task.task_type as keyof typeof TASK_TYPE_LABELS] || task.task_type}
                        </Badge>
                        <Badge
                          className={cn("text-xs", INSTALLATION_STATUS_COLORS[task.status as keyof typeof INSTALLATION_STATUS_COLORS] || "")}
                        >
                          {INSTALLATION_STATUS_LABELS[task.status as keyof typeof INSTALLATION_STATUS_LABELS] || task.status}
                        </Badge>
                      </div>

                      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4" />
                          <span>{getCustomerDisplayName(task.orders?.customers || null)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          <span>
                            {task.cemeteries?.name}, {task.cemeteries?.city}
                          </span>
                        </div>
                        {task.orders?.customers?.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4" />
                            <a
                              href={`tel:${task.orders.customers.phone}`}
                              className="text-primary hover:underline"
                            >
                              {task.orders.customers.phone}
                            </a>
                          </div>
                        )}
                      </div>

                      {task.notes && (
                        <div className="mt-2 text-sm bg-muted p-2 rounded">
                          <AlertCircle className="h-3 w-3 inline mr-1" />
                          {task.notes}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      {task.status === "pending" && (
                        <Button
                          size="sm"
                          onClick={() =>
                            updateStatusMutation.mutate({ id: task.id, status: "in_progress" })
                          }
                          disabled={updateStatusMutation.isPending}
                        >
                          Aloita
                        </Button>
                      )}
                      {task.status === "in_progress" && (
                        <Button
                          size="sm"
                          variant="default"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() =>
                            updateStatusMutation.mutate({ id: task.id, status: "completed" })
                          }
                          disabled={updateStatusMutation.isPending}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Valmis
                        </Button>
                      )}
                      {task.status === "completed" && (
                        <CheckCircle2 className="h-6 w-6 text-green-600" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Loads */}
      {upcomingLoads && upcomingLoads.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Tulevat kuormat</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {upcomingLoads.map((load) => (
                <div
                  key={load.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                  onClick={() => navigate("/loads/mine")}
                >
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="text-sm font-medium">{getDateLabelFormatter(load.load_date)}</div>
                      <div className="text-xs text-muted-foreground">
                        {format(parseISO(load.load_date), "d.M.")}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{load.installation_count} kohdetta</Badge>
                        {load.total_distance_km && (
                          <span className="text-xs text-muted-foreground">
                            {load.total_distance_km.toFixed(0)} km
                          </span>
                        )}
                      </div>
                      {load.estimated_duration_min && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                          <Clock className="h-3 w-3" />
                          ~{Math.round(load.estimated_duration_min / 60)}h
                        </div>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
